/**
 * EshopUsageWidget.jsx
 * ------------------------------------------------------------------
 * Inline dashboard widget showing the user's E-shop SEO quota
 * usage. Renders one progress bar per active limit + a feature-gate
 * chip per boolean (white_label, custom_domain, api_access).
 *
 * Three states:
 *   1. User has NO eshop plan → upgrade CTA (no bars to render).
 *   2. User has plan, all metrics < 80% → normal layout.
 *   3. Any metric >= 80% → that bar turns amber, ">100%" turns rose
 *      and shows an inline "Upgrade plan / Add addon" pair of links.
 *
 * Pulls fresh data on mount + on a 60s polling interval. No global
 * state — the user dashboard already has its own context and this
 * widget is intentionally self-contained so it can be slotted into
 * the affiliate dashboard, the agency portal, etc. without wiring.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../config";
import { Loader2, Zap, AlertTriangle, CheckCircle, Lock } from "lucide-react";

const LABELS = {
  eshops:                    "E-shopov v účte",
  products_scanned_monthly:  "Produktov scanovaných / mes",
  geo_pages_generated:       "GEO stránok vygenerovaných",
  cities_in_geo_matrix:      "Miest v GEO matrici",
  ai_rank_checks_daily:      "AI rank checks / deň",
  industries:                "Industries",
  competitors_tracked:       "Konkurenti sledovaní",
  blog_articles_monthly:     "Blog articles / mes",
  languages:                 "Jazyky",
  white_label:               "White-label",
  custom_domain:             "Vlastná doména",
  api_access:                "REST API prístup",
};

const BOOL_KEYS = ["white_label", "custom_domain", "api_access"];

const PLAN_LABELS = {
  free:           "Free (žiadny E-shop plán)",
  eshop_starter:  "Štartovný",
  eshop_basic:    "Základný",
  eshop_pro:      "Profi",
  eshop_business: "Business / Agency",
  eshop_lifetime: "Lifetime Profi",
};

export function EshopUsageWidget({ userEmail }) {
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!userEmail) return;
    let cancelled = false;
    async function load() {
      try {
        const r = await axios.get(`${API}/eshop/usage`, {
          headers: { "X-User-Email": userEmail },
        });
        if (!cancelled) setUsage(r.data);
      } catch { if (!cancelled) setUsage(null); }
      finally { if (!cancelled) setLoading(false); }
    }
    load();
    // Poll every 60s so the bar moves as the user scans things in
    // other tabs. Cheap — server returns ~700 B per call.
    const id = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, [userEmail]);

  if (loading) {
    return (
      <div className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm flex items-center gap-3" data-testid="eshop-usage-loading">
        <Loader2 className="w-4 h-4 animate-spin text-purple-400" />
        <span className="text-sm text-gray-400">Načítam využitie…</span>
      </div>
    );
  }

  if (!usage || !usage.plan || usage.plan === "free") {
    return (
      <div className="p-5 bg-gradient-to-br from-purple-900/30 to-gray-900/40 border border-purple-700/40 rounded-sm" data-testid="eshop-usage-no-plan">
        <div className="flex items-start gap-3">
          <Zap className="w-5 h-5 text-purple-400 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-base">E-shop SEO/GEO Specialist</div>
            <div className="text-sm text-gray-400 mt-1">
              Nemáš aktívny E-shop plán. Štartovný balík od €29/mes ti odomkne
              auto-publikovanie GEO stránok pre tvoj e-shop.
            </div>
            <a
              data-testid="eshop-usage-upgrade-cta"
              href="/pricing#eshop"
              className="inline-block mt-3 px-4 py-2 bg-purple-600 hover:bg-purple-500 text-white text-sm font-bold rounded-sm"
            >
              Pozrieť E-shop balíky →
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Active plan — render bars + feature chips
  const numericKeys = Object.keys(usage).filter(
    k => k !== "plan" && k !== "addons" && !BOOL_KEYS.includes(k)
       && usage[k] && typeof usage[k].limit === "number"
  );
  const boolKeys = BOOL_KEYS.filter(k => usage[k] && typeof usage[k].enabled === "boolean");

  return (
    <div className="p-5 bg-gray-900/40 border border-gray-800 rounded-sm space-y-4" data-testid="eshop-usage-widget">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-xs uppercase tracking-wider text-gray-500">
            E-shop SEO · využitie tento mesiac
          </div>
          <div className="font-bold text-base mt-1">
            {PLAN_LABELS[usage.plan] || usage.plan}
            {(usage.addons || []).length > 0 && (
              <span className="ml-2 text-xs font-normal text-purple-400">
                + {usage.addons.length} add-on{usage.addons.length > 1 ? "ov" : ""}
              </span>
            )}
          </div>
        </div>
        <a
          href="/pricing#eshop"
          data-testid="eshop-usage-upgrade-link"
          className="text-xs text-purple-400 hover:underline"
        >
          Upgrade plan →
        </a>
      </div>

      <div className="space-y-2.5">
        {numericKeys.map(key => (
          <UsageBar key={key} label={LABELS[key] || key} data={usage[key]} keyName={key} />
        ))}
      </div>

      {boolKeys.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-800">
          {boolKeys.map(k => (
            <FeatureChip key={k} label={LABELS[k] || k} enabled={usage[k].enabled} />
          ))}
        </div>
      )}
    </div>
  );
}

function UsageBar({ label, data, keyName }) {
  const pct = Math.min(100, Math.max(0, data.pct || 0));
  const over = (data.current || 0) > (data.limit || 0);
  // Colour ramps with severity so the eye lands on whichever metric is
  // actually at risk — green for healthy, amber 80-99%, rose 100%+.
  const colour = over
    ? "bg-rose-500"
    : pct >= 80 ? "bg-amber-500"
    : "bg-emerald-500";
  const showAlert = pct >= 80;
  return (
    <div data-testid={`eshop-usage-bar-${keyName}`}>
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-gray-300">{label}</span>
        <span className={`tabular-nums ${over ? "text-rose-400" : pct >= 80 ? "text-amber-400" : "text-gray-400"}`}>
          {data.current.toLocaleString()} / {data.limit.toLocaleString()}
        </span>
      </div>
      <div className="h-1.5 bg-gray-800 rounded-full overflow-hidden">
        <div
          className={`h-full ${colour} transition-all duration-500`}
          style={{ width: `${Math.max(pct, 2)}%` }}
        />
      </div>
      {showAlert && (
        <div className="flex items-center gap-2 mt-1 text-xs">
          <AlertTriangle className={`w-3 h-3 ${over ? "text-rose-400" : "text-amber-400"}`} />
          <span className={over ? "text-rose-400" : "text-amber-400"}>
            {over ? "Limit prekročený · " : "Blížiš sa k limitu · "}
          </span>
          <a href="/pricing#eshop" className="text-purple-400 hover:underline">
            Upgrade
          </a>
          <span className="text-gray-600">·</span>
          <a href={`/pricing#addon_${keyName}`} className="text-purple-400 hover:underline">
            Pridať addon
          </a>
        </div>
      )}
    </div>
  );
}

function FeatureChip({ label, enabled }) {
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-sm border ${
        enabled
          ? "bg-emerald-900/30 border-emerald-700/50 text-emerald-300"
          : "bg-gray-900/40 border-gray-800 text-gray-500"
      }`}
    >
      {enabled ? <CheckCircle className="w-3 h-3" /> : <Lock className="w-3 h-3" />}
      {label}
    </span>
  );
}

export default EshopUsageWidget;
